/**
 * Reusable Notes editor — used by both the dock's mini-window and the
 * full-screen Hub. Single-column layout: search + filter chips on top,
 * grid of cards below, in-place editing inside each card.
 */
import React, { useMemo, useState } from "react";
import { useProductivity } from "@/context/ProductivityContext";
import {
  MagnifyingGlass, Plus, PushPin, Star, Archive, Trash, Tag, X, Check,
} from "@phosphor-icons/react";

const COLOR_BG = {
  yellow: "bg-amber-100 border-amber-300",
  pink:   "bg-pink-100 border-pink-300",
  green:  "bg-emerald-100 border-emerald-300",
  blue:   "bg-sky-100 border-sky-300",
  purple: "bg-violet-100 border-violet-300",
  orange: "bg-orange-100 border-orange-300",
  grey:   "bg-neutral-100 border-neutral-300",
};
const COLOR_LIST = Object.keys(COLOR_BG);

export default function NotesPanel({ dense = false }) {
  const { notes, createNote, updateNote, deleteNote, refreshNotes } = useProductivity();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pinned | favorite | archived
  const [activeTag, setActiveTag] = useState(null);

  const visible = useMemo(() => {
    let list = notes;
    if (filter === "archived") return notes.filter((n) => n.archived);
    list = list.filter((n) => !n.archived);
    if (filter === "pinned")   list = list.filter((n) => n.pinned);
    if (filter === "favorite") list = list.filter((n) => n.favorite);
    if (activeTag) list = list.filter((n) => (n.tags || []).includes(activeTag));
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((n) =>
        (n.title || "").toLowerCase().includes(ql) ||
        (n.body || "").toLowerCase().includes(ql)
      );
    }
    return list;
  }, [notes, filter, activeTag, q]);

  // Refresh archived state when toggled
  React.useEffect(() => {
    refreshNotes({ archived: filter === "archived" });
  }, [filter, refreshNotes]);

  const allTags = useMemo(() => {
    const s = new Set();
    notes.forEach((n) => (n.tags || []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [notes]);

  return (
    <div className="flex flex-col h-full" data-testid="notes-panel">
      {/* Toolbar */}
      <div className="p-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 border border-[var(--border)] px-2 flex-1 min-w-[160px]">
          <MagnifyingGlass size={14} className="text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search notes…"
            className="flex-1 bg-transparent text-sm py-1.5 outline-none"
            data-testid="notes-search"
          />
        </div>
        <button
          onClick={() => createNote({ color: "yellow" })}
          className="flex items-center gap-1 px-2.5 py-1.5 bg-[var(--accent)] text-white text-xs font-medium"
          data-testid="notes-new-btn"
        >
          <Plus size={12} /> New
        </button>
      </div>

      {/* Filter chips */}
      <div className="px-3 py-2 border-b border-[var(--border)] flex items-center gap-1.5 flex-wrap text-xs">
        {["all", "pinned", "favorite", "archived"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2 py-0.5 border ${filter === f ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--border)] text-[var(--text-muted)]"}`}
            data-testid={`notes-filter-${f}`}
          >
            {f}
          </button>
        ))}
        {allTags.length > 0 && (
          <div className="flex items-center gap-1 ml-2 flex-wrap">
            {activeTag && (
              <button onClick={() => setActiveTag(null)} className="text-xs text-[var(--text-muted)]" title="Clear tag">
                <X size={12} />
              </button>
            )}
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(t === activeTag ? null : t)}
                className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${activeTag === t ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-[var(--text-muted)]"}`}
                data-testid={`notes-tag-${t}`}
              >
                #{t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-3">
        {visible.length === 0 ? (
          <div className="text-center text-xs text-[var(--text-muted)] py-10" data-testid="notes-empty">
            {filter === "archived" ? "No archived notes." : "No notes yet — click New to add one."}
          </div>
        ) : (
          <div className={`grid gap-3 ${dense ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
            {visible.map((n) => (
              <NoteCard
                key={n.id}
                note={n}
                onUpdate={(patch) => updateNote(n.id, patch)}
                onDelete={() => deleteNote(n.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onUpdate, onDelete }) {
  const [title, setTitle] = useState(note.title || "");
  const [body, setBody] = useState(note.body || "");
  const [editingTags, setEditingTags] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const dirty = title !== (note.title || "") || body !== (note.body || "");

  // Auto-save 800ms after the last edit.
  React.useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => { onUpdate({ title, body }); }, 800);
    return () => clearTimeout(t);
  }, [title, body, dirty, onUpdate]);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    const next = [...new Set([...(note.tags || []), t])];
    onUpdate({ tags: next });
    setTagInput("");
  };
  const removeTag = (t) => onUpdate({ tags: (note.tags || []).filter((x) => x !== t) });

  return (
    <div
      className={`border ${COLOR_BG[note.color] || COLOR_BG.yellow} p-3 flex flex-col gap-2 group relative`}
      data-testid={`note-card-${note.id}`}
    >
      {/* Top action row */}
      <div className="flex items-center justify-between gap-1">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="bg-transparent font-medium text-sm outline-none flex-1 min-w-0"
          data-testid={`note-title-${note.id}`}
        />
        <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onUpdate({ pinned: !note.pinned })} className="p-1 hover:bg-black/5" title="Pin" data-testid={`note-pin-${note.id}`}>
            <PushPin size={13} weight={note.pinned ? "fill" : "regular"} />
          </button>
          <button onClick={() => onUpdate({ favorite: !note.favorite })} className="p-1 hover:bg-black/5" title="Favorite" data-testid={`note-fav-${note.id}`}>
            <Star size={13} weight={note.favorite ? "fill" : "regular"} />
          </button>
          <button onClick={() => onUpdate({ archived: !note.archived })} className="p-1 hover:bg-black/5" title="Archive" data-testid={`note-archive-${note.id}`}>
            <Archive size={13} weight={note.archived ? "fill" : "regular"} />
          </button>
          <button onClick={onDelete} className="p-1 hover:bg-red-200" title="Delete" data-testid={`note-delete-${note.id}`}>
            <Trash size={13} />
          </button>
        </div>
      </div>

      {/* Body */}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Take a note…"
        rows={4}
        className="bg-transparent text-sm outline-none resize-none flex-1"
        data-testid={`note-body-${note.id}`}
      />

      {/* Colors + tags */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {COLOR_LIST.map((c) => (
            <button
              key={c}
              onClick={() => onUpdate({ color: c })}
              className={`w-3.5 h-3.5 border ${COLOR_BG[c]} ${c === note.color ? "ring-1 ring-black" : ""}`}
              title={c}
              data-testid={`note-color-${note.id}-${c}`}
            />
          ))}
        </div>
        <button onClick={() => setEditingTags((v) => !v)} className="text-[10px] font-mono uppercase tracking-wider text-black/60 hover:text-black flex items-center gap-1" data-testid={`note-tags-toggle-${note.id}`}>
          <Tag size={11} /> {(note.tags || []).length}
        </button>
      </div>

      {editingTags && (
        <div className="border-t border-black/10 pt-2 flex items-center gap-1 flex-wrap" data-testid={`note-tags-editor-${note.id}`}>
          {(note.tags || []).map((t) => (
            <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-black/5 border border-black/10 flex items-center gap-1">
              #{t}
              <button onClick={() => removeTag(t)} className="hover:text-red-700" data-testid={`note-tag-remove-${note.id}-${t}`}>
                <X size={9} />
              </button>
            </span>
          ))}
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
            placeholder="Add tag…"
            className="bg-transparent text-xs outline-none flex-1 min-w-[80px]"
            data-testid={`note-tag-input-${note.id}`}
          />
          {tagInput && (
            <button onClick={addTag} className="p-0.5 hover:bg-black/5" data-testid={`note-tag-add-${note.id}`}>
              <Check size={12} />
            </button>
          )}
        </div>
      )}

      {/* Saved indicator */}
      <div className="text-[9px] font-mono uppercase tracking-widest text-black/40">
        {dirty ? "saving…" : "saved"}
      </div>
    </div>
  );
}
